import { useCallback } from "react";
import { Button } from "@humansignal/ui";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ConsentPurposesEditor.prefix.css";

/**
 * Editor for the per-purpose consent list on a ConsentTemplate.
 *
 * Each purpose row is a `{ key, label, description, required }`
 * object — same shape stored on the model and rendered as a
 * checkbox in the annotator-side gate. ``key`` is the stable
 * machine-readable identifier we emit into the audit log
 * (``purposes_accepted: ["data_export_outside_eu", ...]``); the
 * label is what the annotator reads. Auto-slug from the label on
 * blur so org admins don't have to think about the key field
 * unless they want to.
 *
 * Shared between project-scoped ConsentSettings and the org-wide
 * OrgConsentPage so both surfaces handle the schema the same way.
 */

export interface PurposeRow {
  key: string;
  label: string;
  description?: string;
  required?: boolean;
}

/**
 * The two purposes the platform acts on (same keys as the backend's
 * ``consent.purposes``): training gates its set on ``model_training``;
 * handing data on outside the organisation gates on ``data_sharing``. Offered as
 * one-click rows so nobody has to guess the key.
 */
export const STANDARD_PURPOSES: PurposeRow[] = [
  {
    key: "model_training",
    label: "Training models",
    description: "My annotations may be used to train models.",
    required: false,
  },
  {
    key: "data_sharing",
    label: "Sharing outside the organisation",
    description: "My accepted annotations may be handed on outside the organisation, including publishing.",
    required: false,
  },
];

const slugify = (s: string) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 64);

export const ConsentPurposesEditor = ({
  value,
  onChange,
  disabled,
}: {
  value: PurposeRow[];
  onChange: (next: PurposeRow[]) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const updateRow = useCallback(
    (idx: number, patch: Partial<PurposeRow>) => {
      const next = (value || []).slice();
      next[idx] = { ...next[idx], ...patch };
      onChange(next);
    },
    [value, onChange],
  );

  const addRow = useCallback(() => {
    onChange([...(value || []), { key: "", label: "", description: "", required: false }]);
  }, [value, onChange]);

  const removeRow = useCallback(
    (idx: number) => {
      const next = (value || []).slice();
      next.splice(idx, 1);
      onChange(next);
    },
    [value, onChange],
  );

  const rows = value || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: "var(--font-size-body-small)", color: "var(--color-neutral-content)" }}>
        Purposes{" "}
        <span style={{ color: "var(--color-neutral-content-subtler)" }}>
          {t("components.consentpurposeseditor.consent_purposes_editor.optional")}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: "var(--font-size-body-small)",
          color: "var(--color-neutral-content-subtler)",
          lineHeight: 1.45,
        }}
      >
        Add one row per processing purpose the user must agree to independently (GDPR Art. 7(2)). Annotators see one
        checkbox per row when signing. Required purposes block the signature server-side until ticked. Leave empty for
        short single-purpose agreements. The two standard purposes are the ones the platform acts on: model training
        uses only annotations whose authors accepted Training models; anything handed on outside the organisation
        carries only those whose authors accepted Sharing outside the organisation. Every export states how far each
        purpose is covered.
      </p>

      {rows.length === 0 ? (
        <div
          style={{
            padding: "10px 12px",
            border: "1px dashed var(--color-neutral-border)",
            borderRadius: 6,
            fontSize: "var(--font-size-body-small)",
            color: "var(--color-neutral-content-subtler)",
            textAlign: "center",
          }}
        >
          No purposes defined. The whole document will be one undifferentiated agreement.
        </div>
      ) : (
        rows.map((row, idx) => (
          <div
            key={idx}
            // Grid columns + the responsive collapse live in the stylesheet
            // (inline styles can't be media-queried). Wide screens keep the
            // 4-up layout; ≤560px stacks the label-block + description full
            // width with Required / Remove side-by-side below.
            className={cn("consent-purposes").elem("row").toClassName()}
            style={{
              padding: 8,
              border: "1px solid var(--color-neutral-border-subtle)",
              borderRadius: 6,
              // The checkbox and the button sit level with the inputs' band.
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <input
                type="text"
                value={row.label}
                onChange={(e) => updateRow(idx, { label: e.target.value })}
                onBlur={() => {
                  if (!row.key && row.label) updateRow(idx, { key: slugify(row.label) });
                }}
                disabled={disabled}
                placeholder={t("components.consentpurposeseditor.consent_purposes_editor.label_shown_to_annotators")}
                style={{
                  height: 32,
                  boxSizing: "border-box",
                  padding: "0 10px",
                  border: "1px solid var(--color-neutral-border)",
                  borderRadius: 5,
                  fontSize: "var(--font-size-body-small)",
                  background: "var(--color-neutral-background)",
                }}
              />
              <input
                type="text"
                value={row.key}
                onChange={(e) => updateRow(idx, { key: slugify(e.target.value) })}
                disabled={disabled}
                placeholder={t("components.consentpurposeseditor.consent_purposes_editor.key_machine_id_autoslugged")}
                style={{
                  height: 24,
                  boxSizing: "border-box",
                  padding: "0 10px",
                  border: "1px solid var(--color-neutral-border-subtle)",
                  borderRadius: 6,
                  fontFamily: "var(--font-family-mono, monospace)",
                  fontSize: "var(--font-size-body-small)",
                  color: "var(--color-neutral-content-subtler)",
                  background: "var(--color-neutral-surface, rgba(0,0,0,0.02))",
                }}
              />
            </div>
            <input
              type="text"
              value={row.description || ""}
              onChange={(e) => updateRow(idx, { description: e.target.value })}
              disabled={disabled}
              placeholder={t("components.consentpurposeseditor.consent_purposes_editor.description_optional")}
              style={{
                height: 32,
                boxSizing: "border-box",
                padding: "0 10px",
                border: "1px solid var(--color-neutral-border)",
                borderRadius: 5,
                fontSize: "var(--font-size-body-small)",
                background: "var(--color-neutral-background)",
              }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--font-size-body-small)" }}>
              <input
                type="checkbox"
                checked={!!row.required}
                onChange={(e) => updateRow(idx, { required: e.target.checked })}
                disabled={disabled}
              />
              Required
            </label>
            <Button variant="negative" look="outlined" size="small" disabled={disabled} onClick={() => removeRow(idx)}>
              Remove
            </Button>
          </div>
        ))
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="neutral" look="outlined" size="small" onClick={addRow} disabled={disabled}>
          + Add purpose
        </Button>
        {STANDARD_PURPOSES.filter((p) => !rows.some((r) => r.key === p.key)).map((p) => (
          <Button
            key={p.key}
            variant="neutral"
            look="outlined"
            size="small"
            disabled={disabled}
            onClick={() => onChange([...rows, { ...p }])}
            title={`${p.description} Key ${p.key}: what the platform checks.`}
          >
            + {p.label}
          </Button>
        ))}
      </div>
    </div>
  );
};
